/* HTTP contract types and request schemas.
 *
 * Authority: 05-api-contracts.md. The endpoint index in its §1.8 is exhaustive —
 * exactly 27 routes. An endpoint or field not here does not exist (F6).
 *
 * All field names are snake_case (AP1). Clients ignore unknown response fields
 * and the server ignores unknown REQUEST fields (§1.1 tolerant reader) — except
 * key-whitelisted maps (settings, admin config), which stay 422 on unknown keys.
 */
import { Type } from '@sinclair/typebox';
import { ID_PATTERNS } from '../ids.js';
const p = (re) => re.source;
const Uuid = () => Type.String({ pattern: p(ID_PATTERNS.uuidV4) });
const Iso = () => Type.String({ format: 'date-time' });
/** Request objects are tolerant: unknown properties are ignored, not rejected. */
const Req = (props, opts = {}) => Type.Object(props, { additionalProperties: true, ...opts });
export const API_VERSION = 1;
export const API_VERSION_HEADER = 'x-ascent-api-version';
export const REQUEST_ID_HEADER = 'x-request-id';
export const CLIENT_VERSION_HEADER = 'x-ascent-client-version';
// ---------------------------------------------------------------------------
// §2 Auth & account
// ---------------------------------------------------------------------------
export const EnrollRequestSchema = Req({
    enrollment_code: Type.String({ minLength: 1, maxLength: 200 }),
    device_name: Type.Optional(Type.String({ minLength: 1, maxLength: 60 })),
});
/** Client-writable settings whitelist — exactly five keys (DR-22). */
export const SettingsPatchSchema = Type.Object({
    new_card_cap: Type.Optional(Type.Integer({ minimum: 0, maximum: 20 })),
    push_due_nudge: Type.Optional(Type.Boolean()),
    push_weekly_digest: Type.Optional(Type.Boolean()),
    push_nudge_time: Type.Optional(Type.String({ pattern: '^([01][0-9]|2[0-3]):[0-5][0-9]$' })),
    deep_block_day: Type.Optional(Type.Union([
        Type.Literal('monday'), Type.Literal('tuesday'), Type.Literal('wednesday'),
        Type.Literal('thursday'), Type.Literal('friday'), Type.Literal('saturday'),
        Type.Literal('sunday'),
    ])),
}, 
// Unknown key here is 422 VALIDATION_FAILED, NOT tolerantly ignored (§2.4).
{ additionalProperties: false });
export const CLIENT_WRITABLE_SETTING_KEYS = [
    'new_card_cap',
    'push_due_nudge',
    'push_weekly_digest',
    'push_nudge_time',
    'deep_block_day',
];
export const ADMIN_WRITABLE_SETTING_KEYS = [
    'token_budget_eur',
    'tutor_model',
    'tutor_review_model',
    'tutor_max_output_tokens',
    'desired_retention',
    'fsrs_params_version',
    'fsrs_params',
];
// ---------------------------------------------------------------------------
// §3.4 Session event batch — the outbox drain. Closed event set (DR-20):
// `review` | `unit_completion`. There is no undo event in v1.
// ---------------------------------------------------------------------------
export const SessionEventSchema = Type.Union([
    Req({
        event_id: Uuid(),
        type: Type.Literal('review'),
        occurred_at: Iso(),
        schema_version: Type.Optional(Type.Integer({ minimum: 1 })),
        payload: Req({
            card_id: Type.String({ pattern: p(ID_PATTERNS.card) }),
            unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
            grade: Type.Integer({ minimum: 1, maximum: 4 }),
            duration_ms: Type.Optional(Type.Integer({ minimum: 0 })),
        }),
    }),
    Req({
        event_id: Uuid(),
        type: Type.Literal('unit_completion'),
        occurred_at: Iso(),
        schema_version: Type.Optional(Type.Integer({ minimum: 1 })),
        payload: Req({ unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }) }),
    }),
]);
export const REVIEW_BATCH_MAX_EVENTS = 500;
/* No `maxItems` here on purpose: the contract says an oversize batch is 413
 * PAYLOAD_TOO_LARGE (§1.5.5), and a schema cap would make it a 400 instead. The
 * handler checks the length explicitly, before schema validation. */
export const ReviewsRequestSchema = Req({
    events: Type.Array(SessionEventSchema),
});
// ---------------------------------------------------------------------------
// §3.5 Quiz attempts
// ---------------------------------------------------------------------------
export const CONFIDENCE_VALUES = [0.5, 0.7, 0.9];
export const QuizAnswerSchema = Req({
    item_id: Type.String({ pattern: p(ID_PATTERNS.quizItem) }),
    answer: Type.Optional(Type.Unknown()),
    confidence: Type.Optional(Type.Union([Type.Literal(0.5), Type.Literal(0.7), Type.Literal(0.9)])),
    /** true only for a code_python item that could not run offline (AP17). */
    skipped: Type.Optional(Type.Boolean()),
});
export const QuizAttemptRequestSchema = Req({
    attempt_id: Uuid(),
    unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
    unit_version: Type.Integer({ minimum: 1 }),
    started_at: Iso(),
    submitted_at: Iso(),
    answers: Type.Array(QuizAnswerSchema, { minItems: 1 }),
    /** Recorded for drift diagnostics, discarded as truth (A14). */
    client_score: Type.Number({ minimum: 0, maximum: 1 }),
});
// ---------------------------------------------------------------------------
// §3.6 Mission progress / §3.7 Lab submissions / §3.8 Calibration pulses
// ---------------------------------------------------------------------------
export const MissionProgressRequestSchema = Req({
    events: Type.Array(Req({
        event_id: Uuid(),
        step_id: Type.String({ pattern: p(ID_PATTERNS.missionStep) }),
        /** locked/available are server-derived — sending them is illegal_transition. */
        status: Type.String(),
        occurred_at: Iso(),
        deliverable: Type.Optional(Type.Unknown()),
        submission_id: Type.Optional(Type.Union([Uuid(), Type.Null()])),
        /** CR-003 fallback: absent on current clients; pinned at first receipt. */
        unit_version: Type.Optional(Type.Integer({ minimum: 1 })),
    }), { maxItems: REVIEW_BATCH_MAX_EVENTS }),
});
export const LabSubmissionRequestSchema = Req({
    submission_id: Uuid(),
    mission_unit_id: Type.Optional(Type.String({ pattern: p(ID_PATTERNS.unit) })),
    step_id: Type.Optional(Type.String({ pattern: p(ID_PATTERNS.missionStep) })),
    body_md: Type.String({ minLength: 1, maxLength: 262144 }),
    artifacts: Type.Optional(Type.Array(Req({
        kind: Type.Union([Type.Literal('link'), Type.Literal('path')]),
        value: Type.String({ minLength: 1 }),
        note: Type.Optional(Type.String()),
    }))),
    submitted_at: Iso(),
    unit_version: Type.Optional(Type.Integer({ minimum: 1 })),
});
export const CalibrationPulseRequestSchema = Req({
    pulse_id: Uuid(),
    answers: Type.Array(Req({
        unit_id: Type.String({ pattern: p(ID_PATTERNS.unit) }),
        item_id: Type.String({ pattern: p(ID_PATTERNS.quizItem) }),
        answer: Type.Optional(Type.Unknown()),
        confidence: Type.Optional(Type.Union([Type.Literal(0.5), Type.Literal(0.7), Type.Literal(0.9)])),
        skipped: Type.Optional(Type.Boolean()),
    }), { minItems: 1, maxItems: 8 }),
    submitted_at: Iso(),
});
// ---------------------------------------------------------------------------
// §7 Admin
// ---------------------------------------------------------------------------
export const LabReviewRequestSchema = Req({
    review_id: Uuid(),
    submission_id: Uuid(),
    /** Must cover exactly the target Unit's rubric entry ids. */
    criterion_scores: Type.Record(Type.String(), Type.Integer({ minimum: 0, maximum: 3 })),
    feedback_md: Type.String({ minLength: 1 }),
    reviewed_at: Iso(),
    // Deliberately no `result` field: the server computes score and passed (DR-18).
});
export const TutorThreadRequestSchema = Req({
    thread_id: Uuid(),
    mode: Type.Union([
        Type.Literal('explain'), Type.Literal('quiz_me'),
        Type.Literal('socratic'), Type.Literal('review'),
    ]),
    unit_id: Type.Optional(Type.String({ pattern: p(ID_PATTERNS.unit) })),
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
});
export const TutorMessageRequestSchema = Req({
    message_id: Uuid(),
    content: Type.String({ minLength: 1, maxLength: 8192 }),
});
// ---------------------------------------------------------------------------
// §6 Push
// ---------------------------------------------------------------------------
export const PushSubscriptionRequestSchema = Req({
    endpoint: Type.String({ format: 'uri', minLength: 1 }),
    keys: Req({
        p256dh: Type.String({ minLength: 1 }),
        auth: Type.String({ minLength: 1 }),
    }),
});
/** The full 27-route index of §1.8 — the contract test asserts equality at M6. */
export const ENDPOINT_INDEX = [
    { method: 'POST', path: '/api/auth/enroll', auth: 'enroll' },
    { method: 'POST', path: '/api/auth/rotate', auth: 'device' },
    { method: 'GET', path: '/api/me', auth: 'device' },
    { method: 'PATCH', path: '/api/settings', auth: 'device' },
    { method: 'GET', path: '/api/atlas', auth: 'device' },
    { method: 'GET', path: '/api/units/:id', auth: 'device' },
    { method: 'GET', path: '/api/session/today', auth: 'device' },
    { method: 'POST', path: '/api/reviews', auth: 'device' },
    { method: 'POST', path: '/api/quiz-attempts', auth: 'device' },
    { method: 'POST', path: '/api/missions/:id/progress', auth: 'device' },
    { method: 'POST', path: '/api/labs/:id/submissions', auth: 'device' },
    { method: 'POST', path: '/api/calibration-pulses', auth: 'device' },
    { method: 'GET', path: '/api/progress', auth: 'device' },
    { method: 'POST', path: '/api/tutor/threads', auth: 'device' },
    { method: 'GET', path: '/api/tutor/threads', auth: 'device' },
    { method: 'GET', path: '/api/tutor/threads/:id', auth: 'device' },
    { method: 'POST', path: '/api/tutor/threads/:id/messages', auth: 'device' },
    { method: 'GET', path: '/api/push/public-key', auth: 'device' },
    { method: 'POST', path: '/api/push/subscriptions', auth: 'device' },
    { method: 'DELETE', path: '/api/push/subscriptions', auth: 'device' },
    { method: 'POST', path: '/api/admin/releases', auth: 'admin' },
    { method: 'POST', path: '/api/admin/releases/:id/activate', auth: 'admin' },
    { method: 'GET', path: '/api/admin/releases', auth: 'admin' },
    { method: 'POST', path: '/api/admin/lab-reviews', auth: 'admin' },
    { method: 'GET', path: '/api/admin/state-export', auth: 'admin' },
    { method: 'PATCH', path: '/api/admin/config', auth: 'admin' },
    { method: 'GET', path: '/api/health', auth: 'public' },
];
/** Milestone each route is introduced in — drives `pnpm test:deploy --milestone`. */
export const ROUTE_MILESTONE = Object.freeze({
    'POST /api/auth/enroll': 0,
    'POST /api/auth/rotate': 0,
    'GET /api/me': 0,
    'GET /api/health': 0,
    'GET /api/atlas': 1,
    'GET /api/units/:id': 1,
    'POST /api/admin/releases': 1,
    'POST /api/admin/releases/:id/activate': 1,
    'GET /api/admin/releases': 1,
    'PATCH /api/settings': 2,
    'GET /api/session/today': 2,
    'POST /api/reviews': 2,
    'POST /api/quiz-attempts': 3,
    'POST /api/calibration-pulses': 3,
    'GET /api/progress': 3,
    'GET /api/admin/state-export': 3,
    'POST /api/tutor/threads': 5,
    'GET /api/tutor/threads': 5,
    'GET /api/tutor/threads/:id': 5,
    'POST /api/tutor/threads/:id/messages': 5,
    'GET /api/push/public-key': 5,
    'POST /api/push/subscriptions': 5,
    'DELETE /api/push/subscriptions': 5,
    'PATCH /api/admin/config': 5,
    'POST /api/missions/:id/progress': 6,
    'POST /api/labs/:id/submissions': 6,
    'POST /api/admin/lab-reviews': 6,
});
//# sourceMappingURL=schemas.js.map